import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import PushAparelhoCard from '../../components/PushAparelhoCard';
import { caminhoInterno, carregarNotificacoes, marcarNotificacoesLidas, useNotificacoes } from '../../utils/notificacoes';
import tokens from '../../theme/tokens';

// "há 5 min", "há 3 h", "ontem", "12/09"
function quando(data) {
  const d = new Date(data);
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  if (h < 48) return 'ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

// Lista das notificações da pelada (as mesmas enviadas pelo OneSignal/push). Abrir a tela marca tudo como lido.
const NotificacoesPage = () => {
  const navigate = useNavigate();
  const { lista, naoLidas, carregado } = useNotificacoes();
  const [destacar, setDestacar] = useState(null); // quantas estavam não lidas ao abrir a tela
  const [toast, setToast] = useState(null);
  const marcou = useRef(false);

  useEffect(() => {
    if (!carregado) carregarNotificacoes();
  }, [carregado]);

  useEffect(() => {
    if (carregado && !marcou.current) {
      marcou.current = true;
      setDestacar(naoLidas);
      marcarNotificacoesLidas();
    }
  }, [carregado, naoLidas]);

  return (
    <AppLayout maxWidth="sm">
      <PageHeader eyebrow="Pelada Riviera" title="Notificações" subtitle="Os avisos da pelada, do mais recente para o mais antigo." />

      <PushAparelhoCard somenteSeInativo sx={{ mb: 2 }} onMessage={(message, severity) => setToast({ message, severity })} />

      {!carregado ? (
        <LoadingState label="Carregando notificações..." />
      ) : lista.length === 0 ? (
        <Card><EmptyState icon={<NotificationsRoundedIcon />} title="Nenhuma notificação ainda" description="Quando a diretoria abrir a pelada ou houver novidades, os avisos aparecem aqui." /></Card>
      ) : (
        <Card>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {lista.map((n, index) => {
              const nova = destacar !== null && index < destacar;
              const destino = caminhoInterno(n.link);
              return (
                <ButtonBase
                  key={n._id}
                  onClick={destino ? () => navigate(destino) : undefined}
                  disabled={!destino}
                  sx={{ width: '100%', justifyContent: 'flex-start', textAlign: 'left', px: 2, py: 1.5, bgcolor: nova ? tokens.color.brandSoft : 'transparent', '&.Mui-disabled': { color: 'inherit' } }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ width: '100%' }}>
                    <Box sx={{ width: 8, height: 8, mt: 0.9, borderRadius: '50%', flexShrink: 0, bgcolor: nova ? tokens.color.brand : 'transparent' }} />
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: nova ? 600 : 400, whiteSpace: 'pre-line' }}>{n.mensagem.trim()}</Typography>
                      <Typography variant="caption" color="text.secondary">{quando(n.createdAt)}</Typography>
                    </Box>
                    {destino && <ChevronRightRoundedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />}
                  </Stack>
                </ButtonBase>
              );
            })}
          </Stack>
        </Card>
      )}

      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default NotificacoesPage;
